import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useEffect, useState } from 'react'

import { CacheKeys } from '../lib/cache'
import { followSseChannel } from '../lib/sseChannel'
import { toCamelCase } from '../lib/typeHelper'
import { getSalesLead } from './salesLead'

const STREAM_URL = '/api/v1/insure/internal/realtime/stream/'
const CALLER_LOOKUP_URL = '/api/v1/insure/internal/realtime/lead-by-number/'

export enum TelephonyVendor {
  AIRCALL = 'aircall',
  DIALPAD = 'dialpad',
}

export interface IncomingCallLead {
  id: string
  legalName: string
  /** Primary contact's name, if known — shown as "contact name (company name)". */
  contactName: string | null
}

export interface IncomingCallCustomer {
  /** Org id — links to /insure/internal/customers/<id>. */
  id: string
  name: string
  /** The driver or org user reachable at this number, if we could name one. */
  contactName: string | null
}

/** Everyone we know at a caller's number. A number can match both sides. */
export interface CallerMatches {
  fromNumber: string
  lead: IncomingCallLead | null
  matchCount: number
  customer: IncomingCallCustomer | null
  customerMatchCount: number
}

export interface IncomingCall extends CallerMatches {
  vendor: TelephonyVendor
  callId: string
}

/**
 * Webhook-independent fallback: resolve a caller number to a lead/customer
 * server-side (same matchers the SSE uses). Lets the bar show who's calling even
 * when the `incoming_call` webhook never paired anyone — we always have the number.
 */
async function getCallerMatches(number: string): Promise<CallerMatches> {
  const response = await axios.get(CALLER_LOOKUP_URL, { params: { number } })
  return toCamelCase(response.data) as CallerMatches
}

/** Resolve `number`; idle until the caller has a number that needs resolving. */
export function useCallerLookup(number: string | null) {
  return useQuery({
    queryKey: ['incoming-call-caller-lookup', number],
    queryFn: () => getCallerMatches(number as string),
    enabled: !!number,
    staleTime: 30_000,
  })
}

export interface OutboundCallLead {
  id: string
  legalName: string
  /** Who we're dialing — the primary contact's name, or the phone number if unnamed. */
  contact: string | null
}

export interface OutboundAutomationState {
  automationId: string
  status: string
  currentLead: OutboundCallLead | null
  /** CONNECTING | CONNECTED | MISSED, or null when no call is in flight. */
  callResult: string | null
  done: number
  total: number
  /** ISO resume time while auto-paused between batches, else null (INS-258). */
  autoPausedUntil: string | null
}

/**
 * Subscribe to the shared per-user real-time SSE channel. Returns the latest
 * `data` payload per event `type` (camelCased). Reconnects on drop. New producers
 * (e.g. Phonic outbound dialing) just add a `type` server-side — no client change.
 */
export function useRealtimeStream(enabled: boolean): Record<string, unknown> {
  const [events, setEvents] = useState<Record<string, unknown>>({})

  useEffect(() => {
    if (!enabled) {
      setEvents({})
      return
    }
    return followSseChannel(STREAM_URL, (event) => {
      const notification = event.data == null ? null : toCamelCase(event.data as Record<string, unknown>)
      setEvents((previous) => ({ ...previous, [event.type]: notification }))
    })
  }, [enabled])

  return events
}

export interface RealtimeState {
  /** Current ringing inbound Aircall/Dialpad call, or null. */
  incomingCall: IncomingCall | null
  /** The user's active outbound-call automation dialing state, or null. */
  outboundAutomation: OutboundAutomationState | null
}

/**
 * One SSE subscription feeding every per-user realtime signal (a single stream
 * so we don't open a connection per consumer). Prefetches the ringing call's
 * paired lead so "Show details" opens instantly.
 */
export function useRealtime(enabled: boolean): RealtimeState {
  const events = useRealtimeStream(enabled)
  const incomingCall = (events.incoming_call as IncomingCall | null | undefined) ?? null
  const outboundAutomation =
    (events.outbound_automation as OutboundAutomationState | null | undefined) ?? null
  const queryClient = useQueryClient()

  useEffect(() => {
    const leadId = incomingCall?.lead?.id
    if (!leadId) return
    void queryClient.prefetchQuery({
      queryKey: [CacheKeys.SALES_LEADS, leadId],
      queryFn: () => getSalesLead(leadId),
    })
  }, [incomingCall?.lead?.id, queryClient])

  return { incomingCall, outboundAutomation }
}
